import { useUserDeviceEventsStream } from '@/api';
import { DownArrowIcon, RightArrowIcon, UpArrowIcon } from '@/Components/Common/Icons';
import { useDashboardModal } from '@/Components/Dashboard/DashboardModal';
import {
  SmartPlugIcon,
  SmartPlugPowerActiveIcon,
  SmartPlugPowerInactiveIcon,
  SmartPlugPowerOffIcon,
  SmartPlugPowerOnIcon,
} from '@/Components/Dashboard/icons';
import {
  DEVICE_STATE_PROPERTY,
  DEVICE_TYPES,
  EVENT_PROPERTIES,
  EVENT_TYPES,
  SMART_PLUG_POWER_CONSUMPTION_THRESHOLD,
} from '@/constants/device';
import { formatEnergy } from '@/utilities/helpers/commonHelper';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { Card } from 'antd';
import { get, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import '../RecentActivity.less';
import {
  formatTime,
  getAccessoryUuids,
  getDateLabel,
  HistoryModalTable,
  LoadingCard,
  RECENT_ACTIVITY_HISTORY_DAYS,
  sortByLatestTimestamp,
} from '../RecentActivityUtils';

const deviceTypes = [DEVICE_TYPES.SMART_PLUG];
// The history only shows LAN state reports, so only those are requested.
const HISTORY_EVENT_TYPES = [EVENT_TYPES.SMART_PLUG.ACCESSORY_STATE_LAN_SMART_PLUG];

const renderCells = (entry, { trans }) => {
  const state = get(entry, 'on', '');
  const StateIcon = state !== '' ? (state ? SmartPlugPowerOnIcon : SmartPlugPowerOffIcon) : null;
  const status = get(entry, 'active', '');
  const StatusIcon =
    status !== '' ? (status ? SmartPlugPowerActiveIcon : SmartPlugPowerInactiveIcon) : null;
  return (
    <>
      <div className="row-cell bp-cell">{get(entry, EVENT_PROPERTIES.LOCATION, '-')}</div>
      <div className="row-cell bp-cell">
        {StateIcon && <StateIcon className="state-icon" />}
        {state !== '' ? (state ? trans('ON') : trans('OFF')) : '-'}
      </div>
      <div className="row-cell bp-cell">
        {StatusIcon && <StatusIcon className="state-icon" />}
        {status !== '' ? (status ? trans('ACTIVE') : trans('INACTIVE')) : '-'}
      </div>
    </>
  );
};

const ModalContent = ({ useruuid, deviceUuids, ...displayProps }) => {
  const trans = useSelector(translator);
  const history = useUserDeviceEventsStream({
    useruuid,
    deviceuuid: deviceUuids,
    eventTypes: HISTORY_EVENT_TYPES,
    days: RECENT_ACTIVITY_HISTORY_DAYS,
  });

  return (
    <HistoryModalTable
      {...displayProps}
      {...history}
      headers={[trans('DATE_L'), trans('LOCATION'), trans('STATE'), trans('STATUS')]}
      renderCells={renderCells}
    />
  );
};

ModalContent.propTypes = {
  useruuid: PropTypes.string,
  deviceUuids: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export const SmartHomeSmartPlugCard = (props) => {
  const { useruuid, timezone, locale, is24HourFormat, accessories, data, isLoading, trendData } =
    props;
  const trans = useSelector(translator);
  const { openDashboardModal } = useDashboardModal();

  // One request covers every smart plug; the API accepts a list of device UUIDs.
  const deviceUuids = useMemo(() => getAccessoryUuids(accessories, deviceTypes), [accessories]);

  const latestEvent = useMemo(() => {
    if (isEmpty(data)) return {};
    const items = Array.isArray(data) ? data : [];
    const filteredData = items.filter((event) =>
      [
        EVENT_TYPES.SMART_PLUG.ACCESSORY_STATE_LAN_SMART_PLUG,
        EVENT_TYPES.SMART_PLUG.ACCESSORY_STATE_UPDATE_SMART_PLUG_ON,
      ].includes(get(event, EVENT_PROPERTIES.MESSAGE_TYPE, ''))
    );
    const sortedData = sortByLatestTimestamp(filteredData, EVENT_PROPERTIES.TIMESTAMP);
    return sortedData[0];
  }, [data]);

  const openHistoryModal = () =>
    openDashboardModal({
      title: (
        <div className="recent-activity-modal-title">
          <SmartPlugIcon className="recent-activity-modal-title-icon" />
          <span>{trans('SMARTPLUG')}</span>
        </div>
      ),
      content: (
        <ModalContent
          useruuid={useruuid}
          deviceUuids={deviceUuids}
          timezone={timezone}
          is24HourFormat={is24HourFormat}
          locale={locale}
        />
      ),
    });

  const handleCardClick = (e) => {
    e.preventDefault();
    openHistoryModal();
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openHistoryModal();
    }
  };

  if (isLoading) return <LoadingCard />;

  const location = get(latestEvent, EVENT_PROPERTIES.LOCATION, '');
  const timestamp = get(latestEvent, EVENT_PROPERTIES.TIMESTAMP, '');
  const dateLabel = getDateLabel(timestamp, trans, timezone, locale);
  const formattedTime = formatTime(timestamp, is24HourFormat, timezone);
  const powerUsage = formatEnergy(get(latestEvent, DEVICE_STATE_PROPERTY.smart_plug_lan, null));
  const onOffState = get(latestEvent, DEVICE_STATE_PROPERTY.smart_plug, null);
  const valueLabel =
    powerUsage !== '-'
      ? Number(powerUsage) > SMART_PLUG_POWER_CONSUMPTION_THRESHOLD
        ? trans('ACTIVE')
        : trans('INACTIVE')
      : typeof onOffState === 'boolean'
        ? onOffState
          ? trans('ON')
          : trans('OFF')
        : '';

  const trendPercent = get(trendData, 'trendPercent', null);
  const isUp = trendPercent > 0;
  const isDown = trendPercent < 0;
  const displayPercent = trendPercent !== null ? Math.abs(trendPercent) : null;

  const CardTitle = (
    <div className="recent-activity-card-header">
      <SmartPlugIcon className="recent-activity-card-icon" />
      <h4 className="recent-activity-card-title">{trans('SMARTPLUG')}</h4>
    </div>
  );

  return (
    <Card
      className="recent-activity-card"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      hoverable
      variant="borderless"
      title={CardTitle}
    >
      <div className="recent-activity-card-body">
        <p className="recent-activity-time">
          {dateLabel} {formattedTime}
        </p>
        {location && <p className="recent-activity-context">{location}</p>}
        <p className="recent-activity-data-value">{valueLabel}</p>
      </div>
      <>
        <hr className="recent-activity-divider" />
        <div className="recent-activity-footer-section">
          <p className="recent-activity-footer-label">{trans('DAILY_USE')}</p>
          <div className="recent-activity-card-footer">
            <span className="recent-activity-comparison">
              {trans('YESTERDAY_VS_LAST_WEEK_AVG')}
            </span>
            {displayPercent !== null ? (
              <span
                className={`recent-activity-trend ${isUp ? 'up' : isDown ? 'down' : 'neutral'}`}
              >
                {isUp ? (
                  <UpArrowIcon className="trend-arrow" />
                ) : isDown ? (
                  <DownArrowIcon className="trend-arrow" />
                ) : (
                  <RightArrowIcon className="trend-arrow" />
                )}
                <span>{displayPercent}%</span>
              </span>
            ) : (
              <span className="recent-activity-trend neutral">-</span>
            )}
          </div>
        </div>
      </>
    </Card>
  );
};

SmartHomeSmartPlugCard.propTypes = {
  useruuid: PropTypes.string,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  accessories: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]),
  isLoading: PropTypes.bool,
  trendData: PropTypes.object,
};
